import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings2, Landmark, Users, Crown, Search } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { formatCPFCNPJ, formatCurrency, handleApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";
import PayerV2Badge from "@/components/PayerV2Badge";
import { useAuth } from "@/context/AuthContext";
import MfaBlockedBanner from "@/components/MfaBlockedBanner";

const FLOW_LABELS = { business_day: "Dia útil", fixed_day: "Dia fixo" };

export default function AdminCompanies({ companies, onRefresh }) {
  const { canEdit: canEditPerm } = usePermission("empresas");
  const { user } = useAuth();
  // 2FA vencido: o backend recusa editar a empresa com 403 (FS-056).
  const mfaBlocked = !!user?.mfa_blocked;
  const canEdit = canEditPerm && !mfaBlocked;
  const [credores, setCredores] = useState([]);
  const [payrollMap, setPayrollMap] = useState({});
  const [headcountMap, setHeadcountMap] = useState({});
  const [searchFilter, setSearchFilter] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState({
    credor_id: "",
    max_anticipation_pct_pf: "", max_anticipation_pct_pj: "",
    payment_flow_pf_type: "business_day", payment_flow_pf_day: "5",
    payment_flow_pj_type: "business_day", payment_flow_pj_day: "5",
    taxa_base: "", indice_ajuste_prazo: "",
    grace_period_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    api.get("/admin/credores").then(r => setCredores(r.data)).catch(() => {});
    api.get("/admin/payroll-by-company").then(r => { setPayrollMap(r.data?.payroll || {}); setHeadcountMap(r.data?.headcount || {}); }).catch(() => {});
  }, [companies]);

  const credorName = (id) => credores.find(c => c.id === id)?.razao_social || "-";

  const openConfig = (company) => {
    setEditCompany(company);
    setSaveError("");
    setShowUsers(false);
    setCompanyUsers([]);
    const pfFlow = company.payment_flow_pf || { type: "business_day", day: 5 };
    const pjFlow = company.payment_flow_pj || { type: "business_day", day: 5 };
    setEditForm({
      cnpj: company.cnpj || "",
      credor_id: company.credor_id || "",
      max_anticipation_pct_pf: String(company.max_anticipation_pct_pf ?? 40),
      max_anticipation_pct_pj: String(company.max_anticipation_pct_pj ?? 100),
      payment_flow_pf_type: pfFlow.type, payment_flow_pf_day: String(pfFlow.day),
      payment_flow_pj_type: pjFlow.type, payment_flow_pj_day: String(pjFlow.day),
      taxa_base: company.taxa_base != null ? String(company.taxa_base) : "",
      indice_ajuste_prazo: company.indice_ajuste_prazo != null ? String(company.indice_ajuste_prazo) : "1.80",
      transaction_fee_brl: company.transaction_fee_brl != null ? String(company.transaction_fee_brl).replace(".", ",") : "0,00",
      grace_period_enabled: company.grace_period_enabled !== false,
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const pf = parseFloat(editForm.max_anticipation_pct_pf);
      const pj = parseFloat(editForm.max_anticipation_pct_pj);
      if (isNaN(pf) || isNaN(pj) || pf < 0 || pj < 0) { toast.error("Valores inválidos"); setSaving(false); return; }
      if (pf > 40) { toast.error("Limite para PF não pode exceder 40%"); setSaving(false); return; }
      await api.put(`/companies/${editCompany.id}`, {
        cnpj: editForm.cnpj || null,
        credor_id: editForm.credor_id || null,
        max_anticipation_pct_pf: pf, max_anticipation_pct_pj: pj,
        payment_flow_pf: { type: editForm.payment_flow_pf_type, day: parseInt(editForm.payment_flow_pf_day) },
        payment_flow_pj: { type: editForm.payment_flow_pj_type, day: parseInt(editForm.payment_flow_pj_day) },
        taxa_base: editForm.taxa_base !== "" ? parseFloat(editForm.taxa_base) : null,
        indice_ajuste_prazo: editForm.indice_ajuste_prazo !== "" ? parseFloat(editForm.indice_ajuste_prazo) : null,
        transaction_fee_brl: editForm.transaction_fee_brl !== "" ? parseFloat(String(editForm.transaction_fee_brl).replace(",", ".")) : 0,
        grace_period_enabled: !!editForm.grace_period_enabled,
      });
      toast.success("Configuração atualizada!");
      setEditCompany(null);
      onRefresh?.();
    } catch (err) {
      const msg = handleApiError(err, "Erro ao salvar");
      setSaveError(msg);
      toast.error(msg);
    }
    setSaving(false);
  };

  const fetchCompanyUsers = async (companyId) => {
    setLoadingUsers(true);
    try {
      const res = await api.get(`/admin/company-users/${companyId}`);
      setCompanyUsers(res.data);
    } catch { setCompanyUsers([]); }
    setLoadingUsers(false);
    setShowUsers(true);
  };

  const toggleMaster = async (accountId, current) => {
    try {
      await api.put(`/admin/company-users/${accountId}/master`, { is_master: !current });
      setCompanyUsers(prev => prev.map(u => u.account_id === accountId ? { ...u, is_master: !current } : u));
      toast.success(!current ? "Promovido a master" : "Master removido");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };



  return (
    <>
      {mfaBlocked && <MfaBlockedBanner action="editar empresas" />}

      <Card className="border-white/5">
        <CardContent className="p-0">
          <div className="flex flex-wrap gap-2 p-4 border-b border-white/5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Buscar empresa ou CNPJ..." className="pl-9 h-8 bg-black/20 border-white/10 text-xs" />
            </div>
          </div>
          <div className="overflow-x-auto">
          <Table className="[&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {[
                  { key: "name", label: "Nome" },
                  { key: "cnpj", label: "CNPJ" },
                  { key: "credor", label: "Credor", nosort: true },
                  { key: "taxa_base", label: "Taxa Base" },
                  { key: "approval", label: "Aprovação", nosort: true },
                  { key: "payroll", label: "Folha Mensal", align: "text-right" },
                  { key: "headcount", label: "Nº Colab.", align: "text-right" },
                  { key: "flow_pf", label: "Fluxo PF", nosort: true },
                  { key: "flow_pj", label: "Fluxo PJ", nosort: true },
                  { key: "config", label: "Config", align: "text-right", nosort: true },
                ].map(col => (
                  <TableHead key={col.key}
                    className={`text-xs uppercase tracking-wider text-muted-foreground ${col.align || ""} ${!col.nosort ? "cursor-pointer hover:text-foreground transition-colors" : ""} select-none`}
                    onClick={() => {
                      if (col.nosort) return;
                      if (sortField === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc");
                      else { setSortField(col.key); setSortDir(col.key === "name" || col.key === "cnpj" ? "asc" : "desc"); }
                    }}>
                    {col.label} {sortField === col.key && (sortDir === "asc" ? "↑" : "↓")}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                let items = companies || [];
                if (searchFilter) items = items.filter(c => c.name.toLowerCase().includes(searchFilter.toLowerCase()) || (c.cnpj || "").includes(searchFilter));
                if (sortField) {
                  items = [...items].sort((a, b) => {
                    let va, vb;
                    if (sortField === "payroll") { va = payrollMap[a.id] || 0; vb = payrollMap[b.id] || 0; }
                    else if (sortField === "headcount") { va = headcountMap[a.id] || 0; vb = headcountMap[b.id] || 0; }
                    else if (sortField === "taxa_base") { va = a.taxa_base || 0; vb = b.taxa_base || 0; }
                    else { va = a[sortField] || ""; vb = b[sortField] || ""; }
                    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
                    return sortDir === "asc" ? va - vb : vb - va;
                  });
                }
                return !items.length ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                    {companies?.length ? "Nenhum resultado" : "Nenhuma empresa aprovada"}
                  </TableCell></TableRow>
                ) : items.map((c) => {
                const pfF = c.payment_flow_pf || {};
                const pjF = c.payment_flow_pj || {};
                return (
                  <TableRow key={c.id} data-testid={`company-row-${c.id}`} className="border-white/5 hover:bg-white/[0.02]">
                    <TableCell className="font-medium">{c.name}<PayerV2Badge status={c.payer_v2} companyId={c.id} /></TableCell>
                    <TableCell className="text-muted-foreground font-mono text-sm">{formatCPFCNPJ(c.cnpj) || "-"}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Landmark className="w-3 h-3" /> {credorName(c.credor_id)}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold text-sm">{c.taxa_base != null ? `${c.taxa_base}%` : "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${c.approval_required ? "bg-yellow-500/15 text-yellow-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                        {c.approval_required ? "Requer Aprovação" : "Automática"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-sm text-emerald-400">{payrollMap[c.id] ? formatCurrency(payrollMap[c.id]) : "-"}</TableCell>
                    <TableCell className="text-right text-sm">{headcountMap[c.id] || 0}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{FLOW_LABELS[pfF.type] || pfF.type} {pfF.day}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{FLOW_LABELS[pjF.type] || pjF.type} {pjF.day}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" data-testid={`config-company-${c.id}`}
                        onClick={() => openConfig(c)} className="h-8 w-8 text-muted-foreground hover:text-blue-400">
                        <Settings2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
              })()}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editCompany} onOpenChange={() => setEditCompany(null)}>
        <DialogContent className="sm:max-w-lg border-white/10 max-h-[85vh] overflow-y-auto" data-testid="company-config-dialog">
          <DialogHeader>
            <DialogTitle>Configurar - {editCompany?.name}</DialogTitle>
            <DialogDescription>Credor, limites de antecipação e fluxo de pagamento</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-black/10 border border-white/5 text-sm">
              <div><span className="text-xs text-muted-foreground block">Email</span>{editCompany?.owner_email || "-"}</div>
              <div><span className="text-xs text-muted-foreground block">Telefone</span>{editCompany?.phone || "-"}</div>
            </div>

            <div className="space-y-2">
              <Label>CNPJ da Empresa</Label>
              <Input data-testid="config-cnpj" disabled={!canEdit}
                value={editForm.cnpj}
                onChange={(e) => setEditForm({ ...editForm, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
                className="bg-black/20 border-white/10 font-mono" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Landmark className="w-4 h-4 text-blue-400" /> Credor</Label>
              <Select value={editForm.credor_id} disabled={!canEdit} onValueChange={(v) => setEditForm({ ...editForm, credor_id: v })}>
                <SelectTrigger data-testid="config-credor-select" className="bg-black/20 border-white/10">
                  <SelectValue placeholder="Selecione o credor..." />
                </SelectTrigger>
                <SelectContent>
                  {credores.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.razao_social} ({c.cnpj})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Limite PF (Máx. 40%)</Label>
                <div className="relative">
                  <Input data-testid="config-pf-input" type="number" step="1" min="0" max="40" disabled={!canEdit}
                    value={editForm.max_anticipation_pct_pf}
                    onChange={(e) => setEditForm({ ...editForm, max_anticipation_pct_pf: e.target.value })}
                    className="bg-black/20 border-white/10 pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Limite PJ</Label>
                <div className="relative">
                  <Input data-testid="config-pj-input" type="number" step="1" min="0" max="100" disabled={!canEdit}
                    value={editForm.max_anticipation_pct_pj}
                    onChange={(e) => setEditForm({ ...editForm, max_anticipation_pct_pj: e.target.value })}
                    className="bg-black/20 border-white/10 pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Taxa Base</Label>
                <div className="relative">
                  <Input data-testid="config-taxa-base" type="number" step="0.01" min="0" disabled={!canEdit}
                    value={editForm.taxa_base}
                    onChange={(e) => setEditForm({ ...editForm, taxa_base: e.target.value })}
                    placeholder="0.00"
                    className="bg-black/20 border-white/10 pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Índice de Ajuste por Prazo</Label>
                <div className="relative">
                  <Input data-testid="config-indice-ajuste" type="number" step="0.01" min="0" disabled={!canEdit}
                    value={editForm.indice_ajuste_prazo}
                    onChange={(e) => setEditForm({ ...editForm, indice_ajuste_prazo: e.target.value })}
                    placeholder="1.80"
                    className="bg-black/20 border-white/10" />
                </div>
                <p className="text-[11px] text-muted-foreground">Recomendado: 1,80. Fórmula: Taxa Base + (Índice / dias ponderados)</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Taxa por Transação (R$)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                <Input data-testid="config-transaction-fee" type="text" inputMode="decimal" disabled={!canEdit}
                  value={editForm.transaction_fee_brl}
                  onChange={(e) => {
                    // Allow only digits, comma and dot — max 2 decimal places
                    let v = e.target.value.replace(/[^\d.,]/g, "");
                    // Normalize: replace comma with dot for internal state
                    const parts = v.replace(",", ".").split(".");
                    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
                    else v = parts.join(".");
                    // Limit to 2 decimal places
                    const dotIdx = v.indexOf(".");
                    if (dotIdx >= 0 && v.length - dotIdx > 3) v = v.slice(0, dotIdx + 3);
                    setEditForm({ ...editForm, transaction_fee_brl: v });
                  }}
                  placeholder="0,00"
                  className="bg-black/20 border-white/10 pl-10" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Valor fixo cobrado por transação (cash-in e cashout). Exemplo: 1,00 = R$ 1,00 por transação.
              </p>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg bg-black/10 border border-white/5 p-3">
              <div className="space-y-1 flex-1 min-w-0">
                <Label className="text-sm font-semibold">Carência de 2 meses para novos colaboradores</Label>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Quando ativa, recém-contratados (até 60 dias de casa) veem o recebível mas não podem antecipar.
                  Desligue para liberar antecipação imediata.
                </p>
              </div>
              <Switch
                data-testid="config-grace-period-switch"
                checked={!!editForm.grace_period_enabled}
                disabled={!canEdit}
                onCheckedChange={(v) => setEditForm({ ...editForm, grace_period_enabled: v })}
              />
            </div>

            {["pf", "pj"].map((t) => (
              <div key={t} className="space-y-3">
                <Label className="text-sm font-semibold">Fluxo de Pagamento {t.toUpperCase()}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={editForm[`payment_flow_${t}_type`]} disabled={!canEdit} onValueChange={(v) => setEditForm({ ...editForm, [`payment_flow_${t}_type`]: v })}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid={`config-${t}-flow-type`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business_day">Dia útil do mês seguinte</SelectItem>
                      <SelectItem value="fixed_day">Dia fixo do mês seguinte</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Input data-testid={`config-${t}-flow-day`} type="number" min="1" max="28" disabled={!canEdit}
                      value={editForm[`payment_flow_${t}_day`]}
                      onChange={(e) => setEditForm({ ...editForm, [`payment_flow_${t}_day`]: e.target.value })}
                      className="bg-black/20 border-white/10 pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                      {editForm[`payment_flow_${t}_type`] === "business_day" ? "º dia útil" : "º dia"}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Users Section */}
            <div className="pt-2">
              <Button variant="outline" onClick={() => showUsers ? setShowUsers(false) : fetchCompanyUsers(editCompany?.id)}
                data-testid="show-company-users-btn"
                className="w-full border-white/10 text-sm">
                <Users className="w-4 h-4 mr-2" /> {showUsers ? "Ocultar Usuários" : "Ver Usuários da Empresa"}
              </Button>
            </div>
            {showUsers && (
              <div className="space-y-3 animate-fade-in">
                {loadingUsers ? (
                  <div className="flex justify-center py-4"><div className="loading-spinner w-5 h-5" /></div>
                ) : companyUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">Nenhum usuário cadastrado</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{companyUsers.length} usuário{companyUsers.length !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="max-h-[240px] overflow-y-auto space-y-1.5 pr-1">
                      {companyUsers.map((u) => (
                        <div key={u.account_id} data-testid={`company-user-${u.account_id}`}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-black/10 border border-white/5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            {u.is_master ? <Crown className="w-3.5 h-3.5 text-yellow-400 shrink-0" /> : <Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate">{u.name}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${u.is_master ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>
                              {u.is_master ? "Master" : "Normal"}
                            </span>
                            {u.blocked && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/15 text-red-400">Bloqueado</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {saveError && (
            <p role="alert" data-testid="company-save-error"
              className="text-sm text-red-400 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              {saveError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCompany(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="save-company-config-btn" onClick={saveConfig} disabled={saving || !canEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {saving ? <div className="loading-spinner" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
